import { RynoraErrorCode } from '@rynora/contracts';

/**
 * Human-readable English copy for every backend error code.
 *
 * Always rendered via `ERRORS[body.code]` — the backend's `body.message`
 * is a fallback for unknown / new codes only. Keeping the wire-code
 * (machine) and the copy (human) decoupled means we can:
 *
 *   - Tweak the copy without a backend deploy.
 *   - Translate later by swapping the catalogue.
 *   - Render different copy in different surfaces (e.g. a friendlier
 *     line on the login screen vs. the API docs).
 *
 * Add a new entry HERE when you add a new code in
 * `@rynora/contracts/errors.ts`. The TypeScript type below catches
 * missing entries at compile time.
 */
export const ERRORS: Record<RynoraErrorCode, string> = {
  // ---- Generic ----
  UNKNOWN: 'Something went wrong. Please try again.',
  INTERNAL_ERROR: 'A server error occurred. The team has been notified.',
  NOT_FOUND: "We couldn't find what you were looking for.",
  BAD_REQUEST: 'That request looked off. Please refresh and try again.',
  RATE_LIMITED: 'Too many requests — please slow down.',

  // ---- Validation ----
  VALIDATION_INVALID_BODY: 'The form contains invalid data.',
  VALIDATION_REQUIRED_FIELD: 'A required field is missing.',
  VALIDATION_INVALID_FORMAT: 'That format is not valid.',

  // ---- Auth ----
  AUTH_REQUIRED: 'Please sign in to continue.',
  AUTH_INVALID_CREDENTIALS: 'Email or password is incorrect.',
  AUTH_EMAIL_NOT_FOUND: "We couldn't find an account with that email.",
  AUTH_EMAIL_ALREADY_EXISTS: 'An account already exists for that email.',
  AUTH_INVALID_TOKEN: 'Your session has expired. Please sign in again.',
  AUTH_INVALID_API_KEY: 'API key is invalid or has been revoked.',
  AUTH_INSUFFICIENT_SCOPE: "You don't have permission to do that.",
  AUTH_RESET_TOKEN_EXPIRED: 'This password-reset link has expired. Please request a new one.',
  AUTH_PROVIDER_DISABLED: "This sign-in method isn't enabled on this Rynora instance.",
  CI_KEY_EXPIRED:
    'This API key was rotated and the 24-hour overlap window has ended. Update your CI to use the new key.',
  CI_KEY_ALREADY_DEPRECATED:
    'This key is already in its rotation overlap window. Wait for the previous rotation to finalise before rotating again.',
  AUTH_NOT_ALLOWLISTED: "Your email isn't on the allowlist for this environment. Contact an admin.",
  AUTH_REGISTRATION_CLOSED: 'New registrations are closed on this environment.',

  // ---- MFA ----
  MFA_CODE_INVALID: 'That code is incorrect. Try the next one shown in your app.',
  MFA_SESSION_EXPIRED: 'Your verification session timed out. Please start again.',
  MFA_NOT_ENROLLED: 'Two-step verification is not enabled for this account.',

  // ---- Billing ----
  BILLING_NOT_CONFIGURED: 'Billing is not configured on this Rynora instance.',
  BILLING_PLAN_LIMIT_EXCEEDED: "You've hit a limit on your current plan. Upgrade to continue.",
  BILLING_INVALID_PLAN: 'That plan is no longer available.',

  // ---- Payments (multi-gateway, Tier 14) ----
  PAYMENT_PROVIDER_NOT_IMPLEMENTED:
    "This payment provider isn't wired up yet. Pick a different provider or contact support.",
  PAYMENT_PROVIDER_NOT_CONFIGURED:
    "This payment provider isn't fully configured yet. An administrator needs to paste credentials in Admin Settings → Payments.",
  PAYMENT_WEBHOOK_INVALID_SIGNATURE: "We couldn't verify the webhook signature, so the event was rejected.",
  PAYMENT_REGION_MISMATCH:
    "The active payment provider doesn't accept this billing region. Switch providers or contact your admin.",

  // ---- Flows / runs ----
  PROJECT_NOT_FOUND: 'This project no longer exists or has been deleted.',
  PROJECT_NAME_TAKEN: 'A project with that name already exists in your workspace.',
  PROJECT_NAME_REQUIRED: 'Please give your project a name.',
  FLOW_NOT_FOUND: 'This flow no longer exists or has been deleted.',
  FLOW_NAME_TAKEN: 'A flow with that name already exists in this project.',
  FLOW_NAME_REQUIRED: 'Please give your flow a name.',
  FLOW_VERSION_NOT_FOUND: "That version of the flow couldn't be found.",
  RUN_NOT_FOUND: 'This run is no longer available.',
  RUN_NO_TRACE: 'No trace was captured for this run.',
  RUN_INVALID_STATE: "The run isn't in a state that supports this action.",

  // ---- Flow review + collaborative editing (migration 046) ----
  FLOW_LEASE_HELD_BY_OTHER:
    'Another teammate is editing this flow right now. Wait for them to finish or take over the editor.',
  FLOW_DRAFT_VERSION_STALE:
    'A newer version of this flow was saved while you were editing. Refresh to see the latest changes.',
  FLOW_REVIEW_REQUIRED: 'This project requires a review before publishing. Please request a review first.',
  FLOW_REVIEW_INSUFFICIENT_APPROVALS:
    'This review needs more approvers before you can publish. Ask another teammate to review.',
  FLOW_REVIEW_SELF_APPROVE_DISABLED:
    "You can't approve your own review on this project. Ask a teammate to review instead.",

  // ---- SCM-style flow review (migration 047 onward) ----
  FLOW_BRANCH_NOT_FOUND: 'This branch no longer exists or has been deleted.',
  FLOW_BRANCH_CONFLICT:
    "This branch's changes overlap with the latest published version. Open a fresh branch from the new version and reapply your edits.",
  FLOW_REVIEW_REVIEWER_REMOVED: "You've been removed from this review and can no longer act on it.",
  FLOW_REVIEW_CHECKS_FAILED: 'Some required checks have not passed yet. Wait for them to finish before merging.',
  FLOW_REVIEW_REQUIRED_REVIEWERS_MISSING:
    'This review still needs approval from at least one required reviewer before it can be merged.',

  // ---- Org / membership ----
  ORG_NOT_FOUND: "We couldn't find that organisation.",
  ORG_FORBIDDEN: "You don't have access to this organisation.",
  ORG_INVITE_INVALID: 'This invitation link is invalid or has been used.',
  ORG_MEMBER_NOT_FOUND: "We couldn't find that member in this organisation.",
  ORG_LAST_OWNER: "You're the last owner — transfer ownership before leaving.",
  ORG_NAME_REQUIRED: 'Please give the organisation a name.',
  ORG_CANNOT_REMOVE_OWNER: "You can't remove the owner of an organisation. Transfer ownership first.",
  ORG_AI_DISABLED: 'AI features are disabled for your organisation by an administrator.',
  AUDIT_SIEM_URL_REJECTED:
    'That URL was rejected. SIEM endpoints must be HTTPS and reachable from the public internet (no localhost or private addresses).',
  AUDIT_SIEM_ENDPOINT_AUTO_DISABLED:
    'This SIEM endpoint was auto-disabled after too many consecutive delivery failures. Fix the underlying issue and re-enable it.',

  DSAR_TICKET_NOT_FOUND:
    "We couldn't find a data-request matching that ticket reference. Double-check the reference from your confirmation email.",
  DSAR_RATE_LIMITED:
    "You've submitted several data requests in a short window. Please wait a few minutes before trying again.",
  DSAR_INVALID_TRANSITION:
    'That status change is not allowed from the current state. Fulfilled, rejected and cancelled requests cannot be re-opened.',
  DSAR_EXTENSION_LIMIT:
    'Data-request response windows can be extended by up to 60 days (90 days total under GDPR Art. 12(3)).',

  // ---- Customer-managed encryption keys (BYOK) ----
  ORG_CMK_UNAVAILABLE:
    "We can't decrypt this row because the org's encryption key has been crypto-shredded after its 30-day disable window expired. Re-enrolling a key will not recover historical data.",
  ORG_CMK_NOT_ENROLLED:
    'This org is using platform-managed encryption keys. Enrol a customer-managed key from the Security page first.',
  ORG_CMK_INVALID_KEY:
    'The key you supplied must be base64-encoded and exactly 256 bits (32 bytes). Generate one with `openssl rand -base64 32`.',
  ORG_CMK_PREREQ_FAILED:
    'Customer-managed encryption keys can only be enrolled by orgs that have already turned on the MFA mandate and configured at least one audit-log SIEM endpoint. Fix the prerequisites listed in the response and try again.',
  ORG_CMK_ALREADY_ENROLLED:
    'This org already has a customer-managed key. To replace it use Rotate; to remove it use Disable (which starts a 30-day grace window before crypto-shredding).',
  ORG_CMK_CONFIRMATION_MISMATCH:
    'The confirmation phrase you typed did not match. Type the value shown in the form exactly (case-sensitive) before continuing.',

  // ---- Platform admin (Rynora staff only) ----
  PLATFORM_ADMIN_REQUIRED: 'This action is only available to Rynora staff.',
  PLATFORM_ADMIN_DISABLED: 'Platform-admin features are disabled on this Rynora instance.',

  // ---- Reports / artefacts ----
  REPORT_NOT_FOUND: 'No report is available for this run.',
  REPORT_ASSET_PATH_REQUIRED: 'A report path is required.',

  // ---- Support / contact ----
  SUPPORT_MESSAGE_REQUIRED: 'Please write a message before sending.',
  SUPPORT_DELIVERY_FAILED: "We couldn't deliver your message. Please try again or email us directly.",

  // ---- Admin config ----
  ADMIN_CONFIG_INVALID_SECTION: 'Unknown admin-config section.',
  ADMIN_CONFIG_INVALID_VALUE: 'That value is not allowed for this setting.',
  ADMIN_CONFIG_PERSIST_FAILED: "We couldn't save your changes. Please retry.",

  // ---- SSO ----
  SSO_INVALID_METADATA: 'The SAML metadata could not be parsed.',
  SSO_DOMAIN_TAKEN: 'That domain is already linked to another organisation.',
  SSO_NOT_CONFIGURED: 'Single sign-on is not configured for this organisation.',

  // ---- SCIM provisioning ----
  SCIM_AUTH_INVALID: 'SCIM bearer token is invalid or has been revoked.',
  SCIM_RESOURCE_NOT_FOUND: 'SCIM resource not found in this organisation.',
  SCIM_USER_CONFLICT: 'A user with that email is already provisioned in another organisation.',
  SCIM_PATCH_UNSUPPORTED: 'SCIM PATCH operation is outside the supported subset.',
  SCIM_REQUIRED_ATTRIBUTE_MISSING: 'A required SCIM attribute is missing.',

  // ---- Audit search (delivery roadmap Tier 2.1) ----
  AUDIT_QUERY_INVALID: "That search query couldn't be parsed. Try plain words or a quoted phrase.",
  AUDIT_CURSOR_INVALID: 'The page link is no longer valid. Start your search again to see fresh results.',
  AUDIT_EXPORT_TOO_LARGE: 'That export is too large. Tighten the date range or surface filter and try again.',

  // ---- Run retention (delivery roadmap Tier 2.2) ----
  RUN_RETENTION_PLAN_CAP_EXCEEDED:
    'That retention window is longer than your plan allows. Pick a shorter value or upgrade your plan.',

  // ---- Parallel cross-browser fan-out (delivery roadmap Tier 2.6) ----
  PARALLEL_NEEDS_TWO_CAPABILITIES:
    'Pick at least two browsers to fan out a parallel run. For a single-browser run, use the regular Run button.',
  PARALLEL_PLAN_LIMIT_EXCEEDED:
    "Your plan doesn't include that many concurrent browsers. Pick fewer browsers or upgrade your plan.",
  PARALLEL_PARENT_NOT_FOUND: 'That parallel run no longer exists. It may have been cancelled or purged by retention.',

  // ---- Mobile emulation tier (delivery roadmap Tier 2.7) ----
  MOBILE_DEVICE_UNKNOWN:
    'That device is no longer in the supported registry. Pick a different device or contact an admin.',
  MOBILE_NETWORK_UNKNOWN: "We didn't recognise that network preset. Pick one of Wi-Fi, 4G, Slow 3G, or Offline.",
  NETWORK_OFFLINE:
    'The step needed the network but the run is set to Offline. Switch the network preset or remove the step.',

  // ---- Data sources / data grid editor ----
  DATA_SOURCE_TOO_LARGE: 'CSV is bigger than 10 MB. Trim the file or split into multiple data sources.',
  DATA_SOURCE_INVALID_CSV: 'CSV is missing a header row, or rows have a different number of columns than the header.',
  DATA_SOURCE_NAME_TAKEN: 'A data source with that name already exists in this project.',
  DATA_SOURCE_SCHEMA_MISMATCH: 'The header row no longer matches the schema. Review the columns and try again.',

  // ---- Visual baseline review ----
  STEP_TYPE_MISMATCH:
    'That step is no longer a visual check. The flow was edited since this run; refresh and try again.',
  VISUAL_DIFF_NOT_FOUND: 'The visual diff for that step is no longer available — the run may have been purged.',
  VISUAL_BASELINE_FORBIDDEN: 'You need editor permission to approve a visual baseline.',

  // ---- Notifications ----
  NOTIFICATION_PREFS_INVALID: 'Some of those preferences are not valid.',
  NOTIFICATION_TYPE_UNKNOWN: 'That notification type is not registered. Add it in admin → Notifications → Types.',
  NOTIFICATION_DISABLED: 'Notifications are disabled. Ask an admin to enable them.',
  NOTIFICATION_UNSUBSCRIBE_INVALID:
    'This unsubscribe link is invalid or has expired. You can update preferences from your account settings.',
  NOTIFICATION_CHANNEL_HOST_BLOCKED: 'That webhook host is not on the allow-list. Ask an admin to add it.',
  NOTIFICATION_MARKETING_DISABLED: 'Marketing campaigns are disabled in this environment.',

  // ---- Integrations ----
  INTEGRATION_NOT_CONFIGURED: 'This integration is not configured on the server.',
  INTEGRATION_DISCONNECTED: 'This integration is not connected for your organisation yet.',
  INTEGRATION_FAILED: "We couldn't reach the integration. Please try again.",

  // ---- API testing / suites ----
  API_TEST_NOT_FOUND: "We couldn't find that API test.",
  API_TEST_INVALID_REQUEST: 'That API request looks invalid.',
  SUITE_NOT_FOUND: "We couldn't find that suite.",

  // ---- Deployments ----
  DEPLOY_ENV_INVALID: 'Unknown deployment environment.',
  DEPLOY_GITHUB_NOT_CONFIGURED: 'GitHub is not configured. Open Deployments → Settings to add a repo + token.',
  DEPLOY_GITHUB_AUTH_FAILED: 'GitHub rejected the saved token. Please rotate it.',
  DEPLOY_DISPATCH_FAILED: "We couldn't trigger that deploy. Try again in a moment.",
  DEPLOY_RUN_NOT_FOUND: 'No matching workflow run was found.',
  DEPLOY_HEALTH_UNREACHABLE: "We couldn't reach that environment's health endpoint.",
  DEPLOY_VERIFY_DISPATCH_FAILED: 'Verification dispatch was rejected by GitHub.',
  DEPLOY_VERIFY_WORKFLOW_NOT_FOUND: "verify.yml isn't on the configured branch yet.",
  DEPLOY_ROLLBACK_NO_CANDIDATE: 'No previous successful deploy is recorded for this environment yet.',
  DEPLOY_ROLLBACK_DISPATCH_FAILED: 'Rollback dispatch was rejected by GitHub.',

  // ---- Custom script step ----
  SCRIPT_RUNTIME_UNAVAILABLE:
    'The script runtime is not installed on this runner. Re-deploy the runner image with TypeScript, Python, or Java support.',
  SCRIPT_TIMEOUT: 'The script took too long and was cancelled. Increase the timeout or simplify the code.',
  SCRIPT_RUNTIME_ERROR: 'The script threw an error during execution. See the run log for details.',
  SCRIPT_OUTPUT_TYPE_MISMATCH: "The script returned a value that doesn't match the declared output type.",
  SCRIPT_OUTPUT_MISSING: 'The script finished without returning one of the declared output variables.',

  // ---- Inline runJS() browser-context step (pending-runjs-step) ----
  RUNJS_DISABLED:
    'The inline runJS step is disabled on this Rynora instance. Ask an admin to enable it under Settings → No-code → runJS.',
  RUNJS_BANNED_PATTERN:
    'The runJS body uses eval(), new Function(), or the Function constructor. Rewrite it without those — the sandbox blocks them anyway.',
  RUNJS_TIMEOUT: 'The runJS step took too long and was cancelled. Increase the per-step timeout or simplify the body.',
  RUNJS_RUNTIME_ERROR: 'The runJS body threw an error during execution. See the run log for the page-side stack.',
  RUNJS_OUTPUT_TYPE_MISMATCH:
    "A runJS output didn't match its declared type. Check the outputs declaration in the step.",

  // ---- Pass 2 / dormant integrations ----
  LLM_NOT_CONFIGURED: 'AI script generation is not enabled on this Rynora instance yet.',
  SANDBOX_NOT_CONFIGURED: 'Hosted script execution is not enabled on this Rynora instance yet.',
  LLM_PROMPT_REJECTED: 'That prompt was rejected by our safety guard. Try rephrasing the request.',

  // ---- Cloud browser execution (Phase 5.5) ----
  CLOUD_BROWSER_NOT_CONFIGURED:
    "Cloud browser execution isn't available on this Rynora instance or your current plan. Upgrade or switch to local mode to continue.",
  CLOUD_BROWSER_QUOTA_EXCEEDED:
    "You've used all your cloud browser minutes for the month. Upgrade your plan or switch to local execution to continue.",
  CLOUD_BROWSER_CONCURRENCY_EXCEEDED:
    'Too many cloud browser sessions running. Wait for one to finish or upgrade your plan for more concurrency.',
  EXECUTION_QUEUE_FULL:
    'The execution queue is full for this organisation. Wait for queued jobs to finish, then try again.',
  EXECUTION_ORG_CONCURRENCY_SATURATED:
    'This organisation already has the maximum number of agent jobs running. New jobs stay queued until capacity frees.',

  // ---- Cross-platform browser capability matrix (migration 053) ----
  CAPABILITY_NOT_SUPPORTED:
    'No browser provider can run this OS / browser combination right now. Pick a different combination or contact an admin.',
  CAPABILITY_PROVIDER_DISABLED:
    'The provider that supports this OS / browser is disabled. Ask an admin to enable it in cloud browser settings.',
  CAPABILITY_REQUIRES_CLOUD:
    'This OS / browser needs a cloud provider, and cloud execution is turned off on this instance.',
  CAPABILITY_REQUIRES_PLAN: 'This OS / browser is on a higher plan. Upgrade or pick a supported combination.',

  // ---- Story → Scenarios authoring (Phase 8.18 terminology) ----
  STORY_FETCH_FAILED: "We couldn't load that story. Check the link or paste the description instead.",
  STORY_NOT_FOUND: "We couldn't find a story with that link in this project.",
  STORY_PARSE_FAILED: "We couldn't parse that story. Try simplifying or pasting the description directly.",
  STORY_CONNECTOR_NOT_CONFIGURED: 'This connector is not configured for your organisation. Ask an admin to connect it.',
  STORY_INGEST_UNSUPPORTED:
    "That file type isn't supported. Upload a PDF, screenshot (PNG/JPEG/WebP), audio (mp3/wav/m4a), or PRD (.md/.txt/.docx).",
  STORY_INGEST_TOO_LARGE: 'That file is too large. Try a smaller PDF / image (8–10 MB) or audio under 25 MB.',
  KNOWLEDGE_DISABLED: 'Product knowledge is turned off on this instance.',
  GLOSSARY_LIMIT: 'This project already has the maximum number of glossary terms.',
  KNOWLEDGE_ENTRY_LIMIT: 'This project already has the maximum number of knowledge entries.',
  KNOWLEDGE_PII_REJECTED:
    'That text looks like a secret. Personas must point at vault keys — never paste passwords or OTPs.',
  LOCATOR_NOT_CACHED: 'No cached locator for that intent. Run the flow once in Rynora, or pass flowId and stepId.',
  LOCATOR_CACHE_DISABLED: 'The locator cache is turned off on this instance.',
  QUALITY_HQ_DISABLED: 'Quality HQ is turned off on this instance.',
  QUALITY_RISK_SLUG_TAKEN: 'A risk with that slug already exists in this project.',
  QUALITY_RISK_NOT_FOUND: 'That quality risk could not be found.',
  SCENARIO_REVIEW_PENDING: 'This scenario already has a review in progress.',
  SCENARIO_REVIEW_SELF_APPROVE: 'The author cannot approve their own review.',
  SCENARIO_REVIEW_COMMENT_REQUIRED: 'Request Changes needs a comment.',
  SCENARIO_REVIEW_STALE:
    'This scenario changed since it was submitted for review. Return it to draft and submit again.',
  SCENARIO_REVIEW_NOT_ALLOWED: 'You are not allowed to decide this scenario review.',
  REQUIRED_SCENARIO_POLICY_CONFLICT: 'That required-scenario policy has conflicting membership.',
  REQUIRED_SCENARIO_POLICY_SCENARIO_FOREIGN: 'That scenario is not in this project.',
  REQUIRED_SCENARIO_POLICY_VERSION_NOT_FOUND: 'That required-scenario policy version could not be found.',
  QD1_SNAPSHOT_NOT_FOUND: 'No frozen quality decision exists for that build.',
  QD1_FREEZE_REFUSED: 'That quality decision could not be frozen — no snapshot was created.',
  QD1_COMPARE_REFUSED:
    'Those quality decisions could not be compared — both must be frozen in the same project and environment.',
  CURRENT_BUILD_IDENTITY_NOT_IN_POPULATION:
    'That build identity is not present in this project’s authoritative run population.',
  CURRENT_BUILD_IDENTITY_INCOMPLETE: 'Current build identity requires buildId, commitSha, and environment.',
  STORY_GENERATE_FAILED: "We couldn't generate Scenario proposals right now. Please retry or simplify the input.",
  STORY_GENERATE_QUOTA: "You've hit the AI generation quota for this period. Try again later or upgrade.",
  MANUAL_TEST_NOT_FOUND: "We couldn't find that manual test case.",
  MANUAL_CYCLE_NOT_FOUND: "We couldn't find that test cycle.",
  MANUAL_EXECUTION_NOT_FOUND: "We couldn't find that execution in the cycle.",
  MANUAL_ATTACHMENT_TOO_LARGE: 'That attachment is too large. Paste a smaller screenshot (under 1.5 MB).',
  MANUAL_BUG_PROVIDER_NOT_CONNECTED:
    "That project tool isn't connected. Connect Jira, Rally, or another tracker in Admin → Integrations.",

  // ---- Trace viewer (Sessions 4a/4b/4c, Migration 081) ----
  TRACE_NOT_FOUND: "That trace doesn't exist (or has been deleted).",
  TRACE_DISABLED:
    'Trace storage is disabled on this Rynora instance. Ask an admin to re-enable it in Admin Settings → Runner.',
  TRACE_STREAMING_FAILED:
    "We couldn't stream the trace bundle. Try downloading the .zip and opening it locally with `npx playwright show-trace`.",

  // ---- Story → Tests preview / share links (2026-05-19) ----
  PREVIEW_RUN_QUOTA: "You've used up the daily preview-run quota. Try again after midnight UTC.",
  PDF_RENDERER_UNAVAILABLE:
    "PDF reports aren't available on this Rynora instance — ask an admin to install puppeteer or fall back to the HTML report.",
  SHARE_LINK_NOT_FOUND: 'That share link is no longer valid. Ask the owner to generate a fresh one.',
  SHARE_LINK_DISABLED:
    'Public share links are disabled on this Rynora instance. Ask an admin to enable them in Admin Settings → Reporting.',
  SHARE_LINK_EXPIRED: 'This share link expired. Ask the owner to issue a fresh one.',
  SHARE_LINK_REVOKED: 'This share link was revoked by the owner.',
  SHARE_LINK_RATE_LIMIT: 'We rate-limited this share link. Try again in a minute.',

  CONTRACT_CHECK_NOT_FOUND: 'Contract check not found.',
  CONTRACT_CHECK_NAME_TAKEN: 'A contract check with that name already exists in this project.',
  CONTRACT_CHECK_KIND_NOT_SUPPORTED:
    "That contract-check mode isn't enabled on this Rynora instance. Mode A (openapi-compare) is always on; Mode B (pact-verify) needs a broker to be configured.",
  CONTRACT_CHECK_SPEC_UNREACHABLE: 'Could not fetch one of the spec URLs.',
  CONTRACT_CHECK_SPEC_INVALID: 'One of the spec URLs did not return parseable OpenAPI 3 JSON.',

  VISUAL_MATRIX_DISABLED:
    "Cross-browser visual matrix isn't enabled on this org. Ask an admin to flip the Visual AI → Matrix kill switch in Admin Settings.",
  VISUAL_MATRIX_TOO_MANY_CELLS:
    'Too many browser cells for one matrix run. Reduce the cell count or ask an admin to raise the per-org cap.',
  VISUAL_MATRIX_ANCHOR_NOT_FOUND:
    'No cell in the matrix matches the requested anchor label. Pick an anchor from the cell list or leave it blank to use the first cell.',

  LOCALIZATION_MATRIX_DISABLED:
    "The localization matrix isn't enabled on this org. Ask an admin to flip the Localization kill switch in Admin Settings.",
  LOCALIZATION_MATRIX_TOO_MANY_LOCALES:
    'Too many locales for one localization matrix run. Reduce the locale list or ask an admin to raise the per-org cap.',
  LOCALIZATION_MATRIX_ANCHOR_NOT_FOUND:
    'No cell in the matrix matches the requested anchor locale. Pick an anchor from the locale list or leave it blank to use the first cell.',
  LOCALIZATION_MATRIX_UNKNOWN_LOCALES:
    'One or more locale tags were not in the canonical locale registry. Use locales from the 13-locale list (en-US, en-GB, es-ES, es-MX, fr-FR, de-DE, it-IT, pt-BR, ja-JP, ko-KR, zh-CN, ar-EG, he-IL).',

  DESIGN_AWARE_DISABLED:
    "Design-aware visual checks aren't enabled on this org. Ask an admin to flip the Design-aware kill switch in Admin Settings.",
  DESIGN_FRAME_TOKEN_MISSING:
    'No Figma personal access token configured. An admin must paste one in Admin Settings → Design-aware visual checks before frames can be resolved.',
  DESIGN_FRAME_URL_INVALID:
    "That doesn't look like a valid Figma frame URL. Copy the share URL directly from Figma — make sure you've selected a frame node before clicking Share.",
  DESIGN_FRAME_RESOLVER_FAILED:
    'Figma rejected the resolve call. Check that the personal access token has read access to the file, then try again.',
  DESIGN_FRAME_NOT_FOUND:
    "Figma returned 200 but couldn't find the referenced node. The frame may have been deleted, renamed, or moved — re-copy the share URL from Figma.",
  DESIGN_FRAME_PROVIDER_UNKNOWN:
    "We couldn't tell which design tool that URL belongs to. Paste a Figma (figma.com), Sketch Cloud (sketch.cloud), or Penpot (design.penpot.app) share URL.",
  DESIGN_PROVIDER_TOKEN_MISSING:
    'No personal access token configured for that design tool. An admin needs to paste one in Admin Settings → Design-aware visual checks.',
  PRIVACY_SCAN_DISABLED:
    'Privacy / compliance scanning is turned off. An admin can enable it in Admin Settings → Privacy.',
  PRIVACY_REPORT_WINDOW_INVALID:
    "That week doesn't look right. Use the ISO-week shape `YYYY-Www` (for example `2026-W22`).",
  PRIVACY_CUSTOM_MATCHER_INVALID:
    'One of the custom privacy matchers has an invalid regex. Fix the pattern in Admin Settings → Privacy and try again.',

  CI_PROVIDER_UNKNOWN:
    "Couldn't auto-detect a CI provider. Pass --ci-provider explicitly or set GITHUB_ACTIONS / GITLAB_CI / JENKINS_HOME so Rynora knows which artifacts to emit.",
  CI_TOKEN_MISSING:
    'CI status-post token missing. Set GITLAB_TOKEN / CI_JOB_TOKEN for GitLab or JENKINS_TOKEN (or JENKINS_USER + JENKINS_PASSWORD) for Jenkins.',
  CI_STATUS_POST_FAILED:
    "Couldn't post a status update to your CI provider. Run output and exit code are unaffected — check the warn line on stderr for the HTTP status.",

  // ---- Annotation sets (defer-flowchart-server-persist) ----
  ANNOTATION_SET_NOT_FOUND: "We couldn't find that annotation set.",
  ANNOTATION_SET_INVALID_PAYLOAD: 'That annotation set payload is invalid. Refresh and try again.',
  ANNOTATION_SET_CONFLICT:
    'Another author saved this annotation set after you started editing. Reload to see the latest version.',

  // ---- Mobile emulation favourites (defer-mobile-favourites) ----
  MOBILE_FAVOURITE_NOT_FOUND: "We couldn't find that mobile favourite.",
  MOBILE_FAVOURITE_LIMIT_REACHED: "You've reached the maximum number of mobile favourites.",
  MOBILE_FAVOURITE_INVALID_PAYLOAD: 'That favourite payload is invalid. Refresh and try again.',

  // ---- Org-level LLM credentials (defer-llm-org-byok) ----
  ORG_LLM_PROVIDER_UNKNOWN: 'That LLM provider is not supported. Pick OpenAI, Groq, or Anthropic.',
  ORG_LLM_CREDENTIAL_INVALID_PAYLOAD:
    'The LLM credential payload is invalid. Check the API key, model, and base URL fields.',
  ORG_LLM_CREDENTIAL_NOT_FOUND: 'No credential is configured for that provider.',

  // ---- Integration OAuth (defer-pickers-oauth-flow) ----
  INTEGRATION_OAUTH_DISABLED:
    "OAuth sign-in for this integration isn't enabled on this Rynora instance. Ask an admin to turn it on.",
  INTEGRATION_PROVIDER_UNKNOWN:
    "That integration provider isn't supported. Pick a provider listed in Admin Settings → Integrations.",
  INTEGRATION_OAUTH_NOT_CONFIGURED:
    'OAuth credentials for this provider are missing. Ask an admin to fill them in under Admin Settings → Integrations.',
  INTEGRATION_OAUTH_FAILED: "We couldn't complete the OAuth handshake. Try connecting again.",
  INTEGRATION_OAUTH_STATE_INVALID: 'Your OAuth session expired. Restart the connect flow from the integrations page.',
  INTEGRATION_CONNECTION_NOT_FOUND: "We couldn't find that integration connection.",

  // ---- Flow templates (defer-templates-user-saved + defer-templates-ai-generated) ----
  FLOW_TEMPLATE_INVALID_PAYLOAD: 'That template payload is invalid. Refresh and try again.',
  FLOW_TEMPLATE_NOT_FOUND: "We couldn't find that flow template.",
  FLOW_TEMPLATE_LIMIT_REACHED: "You've reached the maximum number of saved templates for this org.",
  FLOW_TEMPLATE_GENERATOR_DISABLED:
    "The AI template generator isn't enabled on this Rynora instance. Ask an admin to enable it in Admin Settings → AI.",
  FLOW_TEMPLATE_GENERATOR_INVALID_OUTPUT: "The AI provider returned a template we couldn't parse. Try regenerating.",

  // ---- LLM tool calling (defer-llm-tool-use) ----
  TOOL_USE_DISABLED:
    "Tool calling isn't enabled on this Rynora instance. Ask an admin to enable it in Admin Settings → AI.",
  TOOL_USE_INVALID_REQUEST: 'That tool-call request is invalid. Refresh and try again.',
  TOOL_USE_PROVIDER_FAILED: "The AI provider couldn't complete the tool call. Try again in a moment.",
  TOOL_USE_PROVIDER_UNSUPPORTED:
    "The configured AI provider doesn't support tool calling. Switch to a provider that does in Admin Settings → AI.",

  // ---- Phase 8.20 Intent orphan soak ----
  INTENT_PHASE5_ORPHANS_DISABLED:
    'This legacy Intent API is disabled. Use Intent /parse, /explain, Scenario failure-insight, or Scenario AI instead.',

  // ---- Vision input (defer-debug-vision) ----
  VISION_DISABLED:
    "Vision input isn't enabled on this Rynora instance. Ask an admin to enable it in Admin Settings → AI.",
  VISION_INVALID_REQUEST: 'That vision request is invalid. Refresh and try again.',
  VISION_PROVIDER_FAILED: "The AI provider couldn't process the image. Try again in a moment.",
  VISION_PROVIDER_UNSUPPORTED:
    "The configured AI provider doesn't accept image inputs. Switch to a vision-capable provider in Admin Settings → AI.",

  // ---- Test-observability tier v2 (pending-test-observability-tier) ----
  OBSERVABILITY_AI_REMEDIATION_DISABLED:
    "AI remediation suggestions aren't enabled on this Rynora instance. Ask an admin to enable them in Admin Settings → Observability.",
  OBSERVABILITY_AI_REMEDIATION_QUOTA:
    "You've used today's AI remediation quota. Try again tomorrow or ask an admin to raise the quota.",
  OBSERVABILITY_AI_REMEDIATION_FAILED: "The AI provider couldn't generate a remediation. Try again later.",
  OBSERVABILITY_FIRST_SEEN_WEBHOOK_FAILED:
    'The first-seen webhook delivery failed. Check the webhook URL and secret in Admin Settings → Observability.',
  SENTRY_CONNECTOR_NOT_CONFIGURED:
    'The Sentry connector is not configured. Ask an admin to paste a Sentry token, org slug, project slug, and a target Rynora project in Admin Settings → Prod feedback.',
  SENTRY_CONNECTOR_FETCH_FAILED:
    "We couldn't reach Sentry. Check the token, org slug, and project slug in Admin Settings → Prod feedback.",
  AGENT_EXPLORATORY_DISABLED:
    'The exploratory pass is currently disabled for this org. An admin can enable it in Admin Settings → Agent → Exploratory.',

  // ---- Marketplace (defer-templates-marketplace v0) ----
  MARKETPLACE_TEMPLATE_NOT_FOUND: "We couldn't find that marketplace template.",
  MARKETPLACE_INSERT_INVALID_PAYLOAD: 'That insert request is invalid. Refresh the marketplace and try again.',
  MARKETPLACE_PROJECT_FORBIDDEN: "You don't have access to that project.",

  // ---- Org home region / data-residency ----
  ORG_REGION_MISMATCH:
    'This organisation lives in a different Rynora region. Open the link in the response detail to land on the right cluster.',
  ORG_REGION_MIGRATION_UNAVAILABLE:
    "Cross-region migration isn't generally available yet. Your request was recorded — talk to support if you need to expedite an enterprise design-partner move.",
  ORG_REGION_NOT_AVAILABLE:
    'That region is on the roadmap but not yet live. Email enterprise@rynora.app to register interest as a design partner.',
  ORG_REGION_CONFIRMATION_MISMATCH:
    "The confirmation phrase didn't match the destination region's name. Type the region's display name exactly (case-insensitive) before continuing.",
  ORG_REGION_SAME_AS_CURRENT:
    'This organisation is already in the region you selected. Pick a different destination to move it.',
};

/**
 * Look up a human-readable string from a backend error response. Falls
 * back to the server's `message` field, then to a generic line, so a
 * brand-new code rolling out from the backend never renders blank.
 */
export function lookupErrorMessage(body: { code?: string; message?: string } | null | undefined): string {
  if (!body) return ERRORS.UNKNOWN;
  if (body.code && body.code in ERRORS) {
    return ERRORS[body.code as RynoraErrorCode];
  }
  return body.message || ERRORS.UNKNOWN;
}
